import { useState } from 'react';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { ApiError } from '../../../lib/api';
import { MarkdownEditor, SlugTextField } from '../../broadcasts';
import { SURVEY_SLUG_DEFS } from '../../../lib/surveySlugs';
import type { Survey, SurveyChannel } from '../../../types/survey';
import { resendNonResponders, type ResendInput } from '../api';

// Phase 6B F4 — resend-to-non-responders modal, opened from the Pending metric
// card. Re-invites everyone who hasn't submitted yet, against their EXISTING
// survey link (same token). Channel selection defaults to the survey's original
// channels; an optional "customise message" toggle reveals content editors
// (prefilled from the original) whose values are sent as a per-reminder override
// — the Survey doc is never mutated.
export function ResendModal({
  survey,
  pending,
  onClose,
  onDone,
}: {
  survey: Survey;
  pending: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [channels, setChannels] = useState<SurveyChannel[]>(survey.channels);
  const [customise, setCustomise] = useState(false);
  const [emailSubject, setEmailSubject] = useState(survey.emailSubject);
  const [emailBodyMarkdown, setEmailBodyMarkdown] = useState(survey.emailBodyMarkdown);
  const [smsBody, setSmsBody] = useState(survey.smsBody);

  const mutation = useMutation({
    mutationFn: () => {
      const input: ResendInput = { channels };
      if (customise) input.customMessage = { emailSubject, emailBodyMarkdown, smsBody };
      return resendNonResponders(survey.id, input);
    },
    onSuccess: (res) => {
      toast.success(
        res.reminded > 0
          ? `Reminder queued for ${res.reminded} non-responder${res.reminded === 1 ? '' : 's'}.`
          : 'No reachable non-responders to remind.',
      );
      onDone();
      onClose();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not send reminders.');
    },
  });

  const toggleChannel = (c: SurveyChannel) =>
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const showEmail = channels.includes('email');
  const showSms = channels.includes('sms');

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-slate-800">Resend to non-responders</h3>
        <p className="mt-1 text-sm text-slate-600">
          Re-invites the {pending} recipient{pending === 1 ? '' : 's'} who haven&apos;t submitted yet, using
          their existing survey link. Submitted recipients are skipped.
        </p>

        <div className="mt-4">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Channels</div>
          <div className="mt-2 flex gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={showEmail} onChange={() => toggleChannel('email')} />
              Email
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={showSms} onChange={() => toggleChannel('sms')} />
              SMS
            </label>
          </div>
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={customise} onChange={(e) => setCustomise(e.target.checked)} />
          Customise the reminder message (otherwise the original is reused)
        </label>

        {customise && (
          <div className="mt-4 space-y-4 rounded-md border border-slate-200 bg-slate-50/60 p-4">
            {showEmail && (
              <>
                <div>
                  <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                    Email subject
                  </div>
                  <SlugTextField value={emailSubject} onChange={setEmailSubject} defs={SURVEY_SLUG_DEFS} />
                </div>
                <div>
                  <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                    Email body
                  </div>
                  <MarkdownEditor
                    value={emailBodyMarkdown}
                    onChange={setEmailBodyMarkdown}
                    rows={8}
                    defs={SURVEY_SLUG_DEFS}
                  />
                </div>
              </>
            )}
            {showSms && (
              <div>
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                  SMS body
                </div>
                <SlugTextField
                  value={smsBody}
                  onChange={setSmsBody}
                  multiline
                  smsCounter
                  rows={4}
                  defs={SURVEY_SLUG_DEFS}
                />
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || channels.length === 0}>
            {mutation.isPending ? 'Sending…' : 'Send reminders'}
          </Button>
        </div>
      </div>
    </div>
  );
}
